import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Database, Plug, Rows3, TableIcon } from 'lucide-react';
import { useSources } from '../../hooks/useSource';
import { useSourceTypes } from '../../hooks/useSourceTypes';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { getSourceCategory, type Source } from '../../services/source.service';
import { getLastSyncedAt, getSourceStatus, timeAgo } from './sourceStatus';
import type { PipelineNode } from '../../services/pipelineNode.service';
import { summarizeNumber } from '../../services/formatting.service';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import flatFileIcon from '../../assets/images/connectors/flatfile.png';
import snowflakeIcon from '../../assets/images/snowflake_logo.png';

/** Rows shown inline before we collapse the rest behind a "see all" link. */
const MAX_ROWS = 8;

/** Matches the cap the source detail page uses when listing a source's tables. */
const MAX_SOURCE_NODES = 500;

/**
 * Icon for a source we already hold in memory. Not `SourceIcon`: this card has the
 * whole source list in hand, so rendering from it costs nothing, where SourceIcon
 * would fetch its own copy under a different page size. (It no longer fetches per
 * row — see useSourceIndex — so the gap is one request, not one per source.)
 */
function SourceRowIcon({ source, iconPathByType }: { source: Source; iconPathByType: Record<string, string> }) {
  const category = getSourceCategory(source.type);
  const iconPath =
    source.icon_override ||
    iconPathByType[source.type] ||
    (category === 'flat_file' ? flatFileIcon : category === 'snowflake' ? snowflakeIcon : null);

  if (iconPath) {
    return (
      <img
        src={iconPath}
        alt=""
        className="h-8 w-8 flex-shrink-0 rounded-md object-contain bg-muted p-1"
      />
    );
  }
  return (
    <div className="h-8 w-8 flex-shrink-0 rounded-md bg-muted flex items-center justify-center">
      <Plug className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function SourceRow({
  source,
  iconPathByType,
  tables,
  records,
}: {
  source: Source;
  iconPathByType: Record<string, string>;
  tables: number;
  records: number;
}) {
  const lastSyncedAt = getLastSyncedAt(source);
  const status = getSourceStatus(source, lastSyncedAt, tables > 0 || records > 0);

  const subtitle = status.needsConfiguration
    ? status.label
    : [
        records ? `${summarizeNumber(records)} records` : null,
        tables ? `${tables} ${tables === 1 ? 'table' : 'tables'}` : null,
      ]
        .filter(Boolean)
        .join(' · ') || 'No tables yet';

  return (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-border last:border-b-0">
      <SourceRowIcon source={source} iconPathByType={iconPathByType} />
      <div className="flex-1 min-w-0">
        <Link
          to={`/sources/${source.id}`}
          className="font-medium text-sm text-foreground hover:underline block truncate"
        >
          {source.name}
        </Link>
        <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
      </div>
      {status.needsConfiguration ? (
        <Button variant="outline" size="sm" asChild className="flex-shrink-0">
          <Link to={`/sources/${source.id}`}>Configure</Link>
        </Button>
      ) : (
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <Badge variant={status.variant}>{status.label}</Badge>
          {lastSyncedAt && (
            <span className="text-xs text-muted-foreground">{timeAgo(lastSyncedAt)}</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "What Ellie can see" — the data sources feeding Ellie, with how fresh each one
 * is and which ones still need attention.
 */
export default function WhatEllieCanSee() {
  const isInDraftMode = useIsInDraftMode();
  const { data, isLoading, error } = useSources({ page: 1, pageSize: 100 });
  const { data: sourceTypes } = useSourceTypes();

  // Table/row counts come from the source's SOURCE pipeline nodes — the same
  // numbers the source detail sidebar shows. The counts cached on the source
  // itself (`prod_shape`/`draft_shape`) are only recomputed after a model build,
  // so they read as zero for anything synced since. One request covers every
  // source, and the API scopes nodes to the active branch for us.
  const { data: nodesData, isLoading: isLoadingNodes } = usePipelineNodes({
    pageSize: MAX_SOURCE_NODES,
    filters: { node_type: 'SOURCE' },
  });

  const countsBySourceId = useMemo(() => {
    const counts: Record<string, { tables: number; records: number }> = {};
    for (const node of (nodesData?.records || []) as PipelineNode[]) {
      if (!node.source_id) continue;
      const entry = counts[node.source_id] || (counts[node.source_id] = { tables: 0, records: 0 });
      entry.tables += 1;
      entry.records += node.shape?.total_records || 0;
    }
    return counts;
  }, [nodesData?.records]);

  const iconPathByType = useMemo(() => {
    const map: Record<string, string> = {};
    for (const st of sourceTypes || []) {
      if (st.icon_path) map[st.id] = st.icon_path;
    }
    return map;
  }, [sourceTypes]);

  const { sources, totalTables, totalRecords, unconnectedIntegrationCount } = useMemo(() => {
    const all = data?.records || [];

    const decorated = all.map((source) => {
      // Fall back to the cached shape only when the node list has nothing for
      // this source — i.e. it got cut off by MAX_SOURCE_NODES.
      const shape = isInDraftMode ? source.draft_shape : source.prod_shape;
      const counts = countsBySourceId[source.id] || {
        tables: shape?.total_tables || 0,
        records: shape?.total_source_records || 0,
      };
      const lastSyncedAt = getLastSyncedAt(source);
      const hasData = counts.tables > 0 || counts.records > 0;
      const status = getSourceStatus(source, lastSyncedAt, hasData);
      return {
        source,
        tables: counts.tables,
        records: counts.records,
        lastSyncedAt,
        needsConfiguration: status.needsConfiguration,
        isUnconnectedIntegration: status.isUnconnectedIntegration,
      };
    });

    // Freshest first; anything that still needs setting up sinks to the bottom.
    const sorted = decorated.slice().sort((a, b) => {
      if (a.needsConfiguration !== b.needsConfiguration) return a.needsConfiguration ? 1 : -1;
      const aTime = a.lastSyncedAt?.getTime() ?? 0;
      const bTime = b.lastSyncedAt?.getTime() ?? 0;
      if (aTime !== bTime) return bTime - aTime;
      return a.source.name.localeCompare(b.source.name, undefined, { sensitivity: 'base' });
    });

    return {
      sources: sorted,
      totalTables: decorated.reduce((sum, d) => sum + d.tables, 0),
      totalRecords: decorated.reduce((sum, d) => sum + d.records, 0),
      unconnectedIntegrationCount: decorated.filter((d) => d.isUnconnectedIntegration).length,
    };
  }, [data?.records, isInDraftMode, countsBySourceId]);

  const visible = sources.slice(0, MAX_ROWS);
  const hiddenCount = sources.length - visible.length;

  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h2 className="text-base font-semibold text-foreground">What Ellie can see</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/sources">View All</Link>
        </Button>
      </div>

      {(totalTables > 0 || totalRecords > 0) && (
        <div className="flex items-center gap-2 px-6 pb-4">
          <Badge variant="secondary" className="gap-1 font-normal">
            <TableIcon className="h-3 w-3" />
            {summarizeNumber(totalTables)} {totalTables === 1 ? 'table' : 'tables'}
          </Badge>
          <Badge variant="secondary" className="gap-1 font-normal">
            <Rows3 className="h-3 w-3" />
            {summarizeNumber(totalRecords)} records
          </Badge>
        </div>
      )}

      <div className="border-t border-border">
        {/* Wait for the node counts too, so rows don't flash "not connected". */}
        {isLoading || isLoadingNodes ? (
          <div className="px-6 py-4 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <p className="px-6 py-8 text-sm text-destructive text-center">
            Failed to load data sources.
          </p>
        ) : sources.length === 0 ? (
          <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
            <Database className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Ellie can't see any data yet. Connect a source to get started.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/sources">Add a data source</Link>
            </Button>
          </div>
        ) : (
          <>
            {visible.map((item) => (
              <SourceRow
                key={item.source.id}
                source={item.source}
                iconPathByType={iconPathByType}
                tables={item.tables}
                records={item.records}
              />
            ))}
            {hiddenCount > 0 && (
              <Link
                to="/sources"
                className="block px-6 py-3 text-sm text-primary hover:underline border-t border-border"
              >
                {hiddenCount} more {hiddenCount === 1 ? 'source' : 'sources'}
              </Link>
            )}
          </>
        )}
      </div>

      {unconnectedIntegrationCount > 0 && (
        <div className="px-6 py-4 border-t border-border text-sm text-muted-foreground">
          {unconnectedIntegrationCount === 1
            ? 'One source isn’t connected.'
            : `${unconnectedIntegrationCount} sources aren’t connected.`}{' '}
          Ellie will say so rather than guess.
        </div>
      )}
    </Card>
  );
}
